import { useMutation } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { aiService, type WriteAssistantType, type WriteAssistantContext } from '@/lib/api/services/ai.service';

export type ToneOption = 'professional' | 'direct' | 'friendly' | 'casual';
export type SizeOption = 'concise' | 'moderate' | 'elaborate' | 'detailed';

interface GenerateTextParams {
  type: 'headline' | 'bio' | 'client_bio' | 'project_description';
  context?: {
    currentText?: string;
    customInstructions?: string;
    tone?: ToneOption;
    size?: SizeOption;
    iterationPrompt?: string; // For iterating on existing generated text
    previousGeneratedText?: string; // The previously generated text to iterate on
    projectTitle?: string; // For project description generation
    projectUrl?: string; // For project description generation
  };
}

interface GenerateTextResponse {
  text: string;
  type: 'headline' | 'bio' | 'client_bio' | 'project_description';
}

export function useAIWritingAssistant() {
  const { user } = useUser();
  const [generatingType, setGeneratingType] = useState<'headline' | 'bio' | 'client_bio' | 'project_description' | null>(null);

  const generateMutation = useMutation({
    mutationFn: async ({ type, context }: GenerateTextParams): Promise<GenerateTextResponse> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      setGeneratingType(type);

      // Use aiService which handles authentication via apiClient
      const result = await aiService.generateText(type as WriteAssistantType, context as WriteAssistantContext);
      return result;
    },
    onSuccess: (data) => {
      setGeneratingType(null);
      const typeLabel = data.type === 'headline' ? 'Headline' : data.type === 'client_bio' ? 'Company bio' : data.type === 'project_description' ? 'Project description' : 'Bio';
      toast.success(`${typeLabel} generated successfully!`);
    },
    onError: (error: Error) => {
      setGeneratingType(null);
      console.error('AI writing assistant error:', error);
      toast.error(error.message || 'Failed to generate text. Please try again.');
    },
  });

  const generateHeadline = async (
    currentText?: string, 
    options?: { 
      customInstructions?: string; 
      tone?: ToneOption; 
      size?: SizeOption;
      iterationPrompt?: string;
      previousGeneratedText?: string;
    }
  ): Promise<string | null> => {
    try {
      const result = await generateMutation.mutateAsync({
        type: 'headline',
        context: {
          currentText,
          ...options,
        },
      });
      return result.text || null;
    } catch (_error) {
      return null;
    }
  };

  const generateBio = async (
    currentText?: string, 
    options?: { 
      customInstructions?: string; 
      tone?: ToneOption; 
      size?: SizeOption;
      iterationPrompt?: string;
      previousGeneratedText?: string;
    }
  ): Promise<string | null> => {
    try {
      const result = await generateMutation.mutateAsync({
        type: 'bio',
        context: {
          currentText,
          ...options,
        },
      });
      return result.text || null;
    } catch (_error) {
      return null;
    }
  };

  const generateClientBio = async (
    currentText?: string, 
    options?: { 
      customInstructions?: string; 
      tone?: ToneOption; 
      size?: SizeOption;
      iterationPrompt?: string;
      previousGeneratedText?: string;
    }
  ): Promise<string | null> => {
    try {
      const result = await generateMutation.mutateAsync({
        type: 'client_bio',
        context: {
          currentText,
          ...options,
        },
      });
      return result.text || null;
    } catch (_error) {
      return null;
    }
  };

  const generateProjectDescription = async (
    currentText?: string, 
    options?: { 
      customInstructions?: string; 
      tone?: ToneOption; 
      size?: SizeOption;
      projectTitle?: string;
      projectUrl?: string;
      iterationPrompt?: string;
      previousGeneratedText?: string;
    }
  ): Promise<GenerateTextResponse | undefined> => {
    return generateMutation.mutateAsync({
      type: 'project_description',
      context: {
        currentText,
        ...options,
      },
    });
  };

  return {
    generateHeadline,
    generateBio,
    generateClientBio,
    generateProjectDescription,
    isLoading: generateMutation.isPending,
    generatingType,
    data: generateMutation.data,
    reset: generateMutation.reset,
  };
}

